import { appliedFilterLabels, FILTER_LABELS, filterValue } from '../lib/searchFilterLabels';
import { capacityText } from '../lib/capacity';
import RequestProgress from '../components/RequestProgress';
import VenueComparison from '../components/VenueComparison';
import {TrackRecord,useTrackRecords} from '../components/CooperationTools';
import VenueTerms from '../components/VenueTerms';
import SpeechInput from '../components/SpeechInput';
import { useAuth } from '../contexts/AuthContext';
import InterestDialog, { InterestSuccess } from '../components/InterestDialog';
import { readWorkspace, writeWorkspace, searchSignature } from '../lib/workspaceStorage';
import { projectOfferStatuses } from '../lib/applicationState';
import VenuePhoto from '../components/VenuePhoto';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import RequestError from '../components/RequestError';
import { emptySearchFilters, projectSearchFilters, mergeProjectSearchFilters } from '../lib/projectSearch';
import { PlanQuotaBadge } from '../components/PlanBadges';
import CandidateCard, { CandidateZero, isExternalCandidate } from '../components/CandidateCard';
import { Link } from 'react-router-dom';
import {
  Search, Filter, Sparkles, CheckCircle2, Wrench,
  AlertTriangle, ArrowRight, Send,
} from 'lucide-react';
import {
  EVENT_TYPES_CANON, DAYS_CANON, TIME_RANGES_CANON, TECH_CANON,
  BUDGET_MODELS_CANON, DISTRICTS_CANON, TECH_LABELS,
} from '../constants/canonicalEnums';

const API = process.env.REACT_APP_BACKEND_URL;

const labelList = (arr, table) => (arr || []).map(v => table[v] || v);

const emptyFilters = emptySearchFilters;
// A7 (§2.3 d): kullanıcı bir koşulu açıkça "kesin" işaretlerse o koşul eleme yapar; işaretlemezse yalnız kanıt/uyarıdır.
const HARD_TOGGLES = [
  ['days', 'Gün ve saat seçimim kesin', { days: true, time_ranges: true }, ['days', 'time_ranges', 'preferred_days', 'excluded_days', 'preferred_time_ranges']],
  ['budget', 'Bütçe koşulum kesin', { budget: true }, ['budget', 'venue_budget_model', 'venue_budget_max_try']],
  ['technical', 'Teknik ihtiyaçlarım kesin', { technical: true }, ['technical', 'technical_needs']],
];
const hardChecked = (hard, keys) => {
  if (Array.isArray(hard)) return keys.some(k => hard.includes(k));
  return keys.some(k => hard?.[k] === true);
};
const hardDict = hard => (Array.isArray(hard) ? Object.fromEntries(hard.map(k => [k, true])) : { ...(hard || {}) });
const hasHard = hard => (Array.isArray(hard) ? hard.length > 0 : Object.values(hard || {}).some(v => v === true));

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 text-sm rounded-full border-2 transition-colors ${
      active
        ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
        : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
    }`}
  >
    {children}
  </button>
);

const TriChip = ({ value, onChange, label, testIdBase }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-sm font-medium min-w-32">{label}</span>
    {[
      { v: null, l: 'Fark etmez' },
      { v: true, l: 'Evet' },
      { v: false, l: 'Hayır' },
    ].map(opt => (
      <Chip
        key={String(opt.v)}
        active={value === opt.v}
        onClick={() => onChange(opt.v)}
        testId={`${testIdBase}-${opt.v}`}
      >
        {opt.l}
      </Chip>
    ))}
  </div>
);

const CARD_SHELL = 'bg-white border border-zinc-100 hover:border-brand/40 hover:shadow-md transition-all duration-300 rounded-2xl p-6 animate-soft-lift';
const CARD_TITLE = 'font-serif text-2xl tracking-tight text-zinc-900';

// A7: ortak aday kartı. Numerik uyum skoru yok; kanıt satırları backend `facts` alanından gelir.
const VenueCard = ({ v, onInterest, alreadyApplied, stale, onDetail, compared, onCompare, compareFull, trackRecord }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try { await onInterest(v); } finally { setBusy(false); }
  };
  if (isExternalCandidate(v)) {
    // EXT-08: dış kartta uygulama içi teklif yok; doğrulanmış herkese açık bağlantı CTA'dır.
    return <CandidateCard card={v} className={CARD_SHELL} testId={`venue-card-${v.id}`} headingClassName={CARD_TITLE} />;
  }
  return (
    <CandidateCard card={v} className={CARD_SHELL} testId={`venue-card-${v.id}`} headingClassName={CARD_TITLE}
      media={<Link to={`/venues/${v.id}`} state={{ fromSearch: true }} onClick={() => onDetail(v)} className="home-venue-photo search-venue-photo" aria-label={`${v.name} mekânını incele`} data-testid={`venue-card-photo-${v.id}`}>
        <VenuePhoto venue={v} />
      </Link>}
      title={<Link to={`/venues/${v.id}`} state={{ fromSearch: true }} onClick={() => onDetail(v)} className="hover:text-[#1E3A8A] hover:underline transition-colors" data-testid={`venue-card-name-${v.id}`}>
        {v.name}
      </Link>}
      actions={<>
        <Link
          to={`/venues/${v.id}`} state={{ fromSearch: true }} onClick={() => onDetail(v)}
          data-testid={`venue-detail-link-${v.id}`}
          className="px-4 py-3 rounded-xl font-medium text-sm border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#E6EEFF] transition-colors flex items-center gap-1.5 active:scale-[0.98]"
        >
          Detay <ArrowRight size={15} strokeWidth={2} />
        </Link>
        <button
          onClick={handle}
          disabled={busy || alreadyApplied || stale}
          data-testid={`interest-btn-${v.id}`}
          className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2 active:scale-[0.98] ${
            alreadyApplied
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 cursor-default'
              : 'action-primary shadow-sm hover:shadow'
          } disabled:opacity-60`}
        >
          {alreadyApplied
            ? (<><CheckCircle2 size={16} strokeWidth={2} /> {alreadyApplied === 'concluded' ? 'Bu etkinlik için görüşme sonuçlandı' : 'Bu aramada talebin var'}</>)
            : (<><Send size={16} strokeWidth={2} /> {busy ? 'İletiliyor…' : 'Talep hazırla'}</>)
          }
        </button>
        {alreadyApplied && <Link to="/organizer/applications" className="text-action text-xs">Mevcut görüşmeyi aç →</Link>}
      </>}
    >
      {v.min_capacity_flexible && <p className="text-xs text-zinc-500 mb-2">Minimum kişi sayısı esnek; mekânla teyitleş.</p>}
      {v.description && <p className="text-sm text-zinc-700 mb-3 line-clamp-3">{v.description}</p>}
      {v.technical_amenities?.length > 0 && (
        <div className="flex items-start gap-2 text-sm mb-3">
          <Wrench size={14} strokeWidth={1.5} className="text-zinc-400 mt-0.5" />
          <div>
            <span className="text-zinc-500 block text-xs">Öne çıkan imkânlar</span>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {labelList(v.technical_amenities, TECH_LABELS).map(t => (
                <span key={t} className="text-xs px-2 py-0.5 bg-zinc-100 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        </div>
      )}
      <VenueTerms venue={v}/>
      <TrackRecord record={trackRecord}/>
      <label className="compare-pick"><input type="checkbox" checked={compared} disabled={!compared&&compareFull} onChange={onCompare}/>Karşılaştırmaya ekle</label>
      {v.reasons_positive?.length > 0 && (
        <details className="mb-3">
          <summary className="text-xs font-semibold text-emerald-700 cursor-pointer hover:underline flex items-center gap-1.5">
            <CheckCircle2 size={13} strokeWidth={1.75} /> Uyum gerekçeleri
          </summary>
          <ul className="mt-1.5 space-y-0.5 text-xs text-emerald-900 pl-5">
            {v.reasons_positive.map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
        </details>
      )}
      {v.reasons_negative?.length > 0 && (
        <details className="mb-3">
          <summary className="text-xs font-semibold text-amber-700 cursor-pointer hover:underline flex items-center gap-1.5">
            <AlertTriangle size={13} strokeWidth={1.75} /> Dikkat edilmesi gerekenler
          </summary>
          <ul className="mt-1.5 space-y-0.5 text-xs text-amber-900 pl-5">
            {v.reasons_negative.map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
        </details>
      )}
    </CandidateCard>
  );
};

const OrganizerSearch = () => {
  const location = useLocation();
  const prefill = location.state?.prefill || null; // Fikir/proje detayından gelen başlangıç verisi
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const requestedProjectId = searchParams.get('project') || prefill?.project_id || null;
  const cacheKey = `search:${user?.id || ''}:${requestedProjectId || 'general'}`;
  const [cached] = useState(() => {
    const c = readWorkspace(cacheKey);
    return c && c.entry === (location.key || '') && (c.projectCtx?.id || null) === requestedProjectId ? c : null;
  });
  const [snapshot, setSnapshot] = useState(cached?.snapshot || null);
  const [pendingVenue, setPendingVenue] = useState(null);
  const [successVenue, setSuccessVenue] = useState(null);
  const [projectCtx, setProjectCtx] = useState(cached?.projectCtx || (prefill?.project_id ? { id: prefill.project_id, title: prefill.project_title } : null));
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [freeText, setFreeText] = useState(cached?.freeText ?? prefill?.free_text ?? '');
  const [filters, setFilters] = useState(() => cached?.filters || projectSearchFilters(prefill || {}));
  const [showFilters, setShowFilters] = useState(!!(prefill && (prefill.event_type || prefill.expected_attendees || prefill.preferred_districts?.length)));
  const [searching, setSearching] = useState(false);
  const [moreBusy, setMoreBusy] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [needsCapacity, setNeedsCapacity] = useState(false);
  const capacityInput = useRef(null);
  const [quotaTick, setQuotaTick] = useState(0);
  const [results, setResults] = useState(cached?.results || null); // {venues, applied_filters, extracted_filters, follow_up_questions}

  useEffect(() => {
    if (needsCapacity && showFilters) capacityInput.current?.focus();
  }, [needsCapacity, showFilters]);

  // Only an explicitly opened project supplies constraints. General discovery starts fresh.
  useEffect(() => {
    if (prefill || cached) return;
    const pid = searchParams.get('project');
    if (!pid) return;
    let cancelled = false;
    axios.get(`${API}/api/organizer/event-projects/${pid}`).then(({ data: p }) => {
      if (cancelled) return;
      setProjectCtx({ id: p.id, title: p.title });
      setFreeText(prev => prev || [p.title, p.description].filter(Boolean).join(' — '));
      setFilters(f => mergeProjectSearchFilters(f, p));
      if (p.event_type || p.expected_attendees || p.preferred_districts?.length) setShowFilters(true);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [applied, setApplied] = useState({}); // { venue_id: 'open' | 'concluded' } — bu projede teklifi olan mekânlar

  const [compared,setCompared]=useState(cached?.compared||[]);
  useEffect(()=>{if(results?.venues)setCompared(old=>old.filter(id=>results.venues.some(v=>v.id===id)));},[results]);
  const trackRecords=useTrackRecords((results?.venues||[]).filter(v=>!isExternalCandidate(v)).map(v=>v.id));
  const stale = !!snapshot && snapshot.signature !== searchSignature(freeText, filters);
  useEffect(() => { writeWorkspace(cacheKey, { entry: location.key || '', freeText, filters, results, snapshot, projectCtx, compared }); }, [cacheKey, location.key, freeText, filters, results, snapshot, projectCtx, compared]);
  useEffect(() => { if (cached?.scroll) requestAnimationFrame(() => window.scrollTo(0, cached.scroll)); }, [cached]);
  const rememberDetail = (v) => { writeWorkspace(cacheKey, { entry: location.key || '', freeText, filters, results, snapshot, projectCtx, compared, scroll: window.scrollY }); writeWorkspace(`application-context:${v.id}`, stale ? null : snapshot); };
  const toggleList = (key, value) => {
    setFilters(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value] };
    });
  };

  const runSearch = async (selectedFilters = filters, acceptFormFilters = false, textOverride = null) => {
    if (searching || voiceBusy || moreBusy) return;
    const text = textOverride ?? freeText;
    setSearchError('');
    setNeedsCapacity(false);
    if (!text.trim() && !Object.entries(selectedFilters).some(([k, v]) => k === 'hard' ? hasHard(v) : Array.isArray(v) ? v.length > 0 : v !== '' && v != null && v !== false && v !== 0)) {
      toast.error('En az bir arama kriteri gerekli — serbest metin yaz ya da bir filtre seç.');
      return;
    }
    setSearching(true);
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(selectedFilters).filter(([k, v]) =>
          v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0) && (k !== 'hard' || hasHard(v))
        ).map(([k, v]) => {
          if (k === 'hard') return [k, hardDict(v)];
          if (['preferred_neighborhoods', 'excluded_neighborhoods', 'excluded_districts'].includes(k) && typeof v === 'string') return [k, v.split(',').map(s => s.trim()).filter(Boolean)];
          return k === 'expected_attendees' && v !== '' ? [k, Number(v)] : [k, v];
        })
      );
      const { data } = await axios.post(`${API}/api/organizer/search`, {
        free_text: text.trim() || null,
        filters: cleanFilters,
        accept_form_filters: acceptFormFilters === true,
        // A7: proje bağlamında "bu projede reddedilenler" sunucuda düşülür (yalnız projenin sahibi için).
        ...(projectCtx?.id ? { project_id: projectCtx.id } : {}),
      }, { timeout: 45000 });
      setResults(data);
      setSnapshot({ signature: searchSignature(text, selectedFilters), free_text: text.trim(), project_id: projectCtx?.id, project_title: projectCtx?.title, applied_filters: data.applied_filters || {}, extracted_filters: data.extracted_filters || {} });
      // Secondary lookup must not block usable search results. Server prevents duplicate applications.
      axios.get(`${API}/api/organizer/interests`, { timeout: 10000 }).then(({ data: mine }) => {
        // A10: tek kanonik kural (sunucunun aynası, lib/applicationState.projectOfferStatuses).
        setApplied(projectOfferStatuses(mine, projectCtx?.id));
      }).catch(() => {});
    } catch (err) {
      if (['CAPACITY_CLARIFICATION_REQUIRED', 'LOCATION_CLARIFICATION_REQUIRED', 'FILTER_CLARIFICATION_REQUIRED'].includes(err.response?.data?.detail?.code)) {
        setResults(null);
        setSnapshot(null);
        setShowFilters(true);
        setNeedsCapacity(err.response?.data?.detail?.code === 'CAPACITY_CLARIFICATION_REQUIRED');
      }
      const message = apiErrorMessage(err, 'Arama başarısız.');
      setSearchError(message);
      toast.error(message);
    } finally {
      setSearching(false); setQuotaTick(t => t + 1);
    }
  };

  const submitInterest = (venue) => { if (!stale && !searching && !isExternalCandidate(venue)) setPendingVenue(venue); };

  // A7 (MATCH-07/08): "Daha fazla göster" aynı aramanın sonraki 3 kartını imzalı imleçle getirir;
  // AI çağrısı ve arama hakkı kullanılmaz, gösterilen kart tekrar gelmez, plan tavanı toplam sınırdır.
  const loadMore = async () => {
    if (!results?.cursor || moreBusy || searching || stale) return;
    setMoreBusy(true); setSearchError('');
    try {
      const { data } = await axios.post(`${API}/api/organizer/search`, { cursor: results.cursor }, { timeout: 30000 });
      setResults(prev => {
        const seen = new Set((prev?.venues || []).map(v => v.id));
        return { ...prev, venues: [...(prev?.venues || []), ...(data.venues || []).filter(v => !seen.has(v.id))],
          cursor: data.cursor || null, has_more: !!data.has_more, shown: data.shown, total: data.total ?? prev?.total,
          limit_reached: !!data.limit_reached, pool_changed: !!data.pool_changed };
      });
    } catch (err) {
      const message = apiErrorMessage(err, 'Diğer sonuçları getiremedim. Aramayı yeniden yapabilirsin.');
      setSearchError(message); toast.error(message);
    } finally { setMoreBusy(false); }
  };

  // MATCH-14: 0 sonuç önerisi yalnız kullanıcı düğmeye basınca uygulanır. Metin, gevşetilen koşulu yeniden
  // dayatmasın diye son aramada yorumlanan seçimler forma taşınır, metin kutusu boşalır ve yeni arama yapılır.
  const widen = suggestion => {
    const base = { ...emptyFilters };
    for (const [k, v] of Object.entries(results?.applied_filters || {})) if (k in base || k === 'hard') base[k] = v;
    const next = { ...base };
    for (const [k, v] of Object.entries(suggestion.filters || {})) {
      if (['region', 'date_text', 'event_types'].includes(k)) continue;
      if (k === 'preferred_districts' && suggestion.filters.region?.length) { next.preferred_districts = suggestion.filters.region; continue; }
      if (k === 'hard') { next.hard = hasHard(v) ? hardDict(v) : undefined; continue; }
      next[k] = v == null ? (Array.isArray(emptyFilters[k]) ? [] : '') : v;
    }
    if (Array.isArray(next.hard)) next.hard = hasHard(next.hard) ? hardDict(next.hard) : undefined;
    setFilters(next); setFreeText(''); setShowFilters(true);
    runSearch(next, true, '');
  };
  const toggleHard = (group, keys) => setFilters(prev => {
    const hard = hardDict(prev.hard);
    if (hardChecked(prev.hard, keys)) keys.forEach(k => { delete hard[k]; });
    else Object.assign(hard, group);
    return { ...prev, hard: hasHard(hard) ? hard : undefined };
  });

  return (
    <div className="organizer-search min-h-[calc(100vh-4rem)] bg-[#FAFAFA]">
      <div className="max-w-4xl mx-auto p-6 sm:p-10">
        <InterestDialog venue={pendingVenue} context={snapshot} onClose={() => setPendingVenue(null)} onSuccess={v => { setApplied(prev => ({ ...prev, [v.id]: 'open' })); setSuccessVenue(v); setPendingVenue(null); }} />
        <InterestSuccess venue={successVenue} />
        {stale && <div role="status" className="home-notice">Kriterlerin değişti. Başvurmadan önce sonuçları güncelle.<button onClick={() => runSearch()} disabled={searching || voiceBusy} className="text-action">Sonuçları güncelle</button></div>}
        <div className="text-center mb-10 animate-soft-lift" data-testid="search-header">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3 text-zinc-900" style={{ fontVariationSettings: '"opsz" 96' }}>
            Etkinliğin için doğru mekânı bul
          </h1>
          <p className="text-zinc-600 text-base sm:text-lg max-w-2xl mx-auto">
            Ne yapmak istediğini yaz, sana uygun mekânları filtreleyelim.
          </p>
          <div className="mt-4"><PlanQuotaBadge refreshKey={quotaTick} keys={[{ key: 'match_search_daily', label: 'arama' }]} /></div>
        </div>

        {projectCtx && (
          <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-xl border border-[#1E3A8A]/20 bg-[#E6EEFF] text-sm" data-testid="search-project-context">
            <span className="text-[#1E3A8A] truncate"><span className="font-semibold">Proje:</span> {projectCtx.title}</span>
            <Link to={`/organizer/projects/${projectCtx.id}`} className="text-xs font-medium text-[#1E3A8A] hover:underline shrink-0" data-testid="search-project-context-link">Proje detayı →</Link>
          </div>
        )}

        <RequestError message={searchError} testId="search-error" />
        {/* Search box */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm hover:shadow transition-shadow mb-4 animate-soft-lift stagger-1">
          <textarea
            value={freeText}
            onChange={e => setFreeText(e.target.value)}
            placeholder="30 kişilik heykel workshop'u yapmak istiyorum. Beşiktaş civarı olsun, masa düzeni uygun olsun, mekân kira istemesin."
            rows={3}
            data-testid="search-freetext"
            className="w-full px-3 py-2 text-base focus:outline-none resize-none placeholder:text-zinc-400 bg-transparent"
          />
          <SpeechInput disabled={searching || voiceBusy} onBusyChange={setVoiceBusy} onTranscript={text => setFreeText(prev => [prev.trim(), text].filter(Boolean).join(' ').slice(0, 4000))} />
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2 border-t border-zinc-100 pt-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-[#1E3A8A] transition-colors"
            >
              <Filter size={15} strokeWidth={1.75} />
              Filtreleri {showFilters ? 'gizle' : 'göster'}
            </button>
            <button
              onClick={() => runSearch()}
              disabled={searching || voiceBusy}
              data-testid="search-submit"
              className="action-primary inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 shadow-sm hover:shadow active:scale-[0.98] disabled:opacity-60"
            >
              <Search size={16} strokeWidth={2} />
              {searching ? 'Aranıyor…' : 'Mekân Ara'}
            </button>
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-5 mb-6 space-y-4" data-testid="filters-panel">
            <div>
              <label className="text-sm font-semibold block mb-2">Etkinlik türü</label>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPES_CANON.map(o => (
                  <Chip key={o.value} active={filters.event_type === o.value}
                    onClick={() => setFilters(f => ({ ...f, event_type: f.event_type === o.value ? '' : o.value }))}
                    testId={`filter-event-${o.value}`}
                  >{o.label}</Chip>
                ))}
              </div>
            </div>

            <div className="flex gap-4 flex-wrap">
              <div>
                <label htmlFor="search-attendees" className="text-sm font-semibold block mb-2">Katılımcı sayısı</label>
                <input id="search-attendees" ref={capacityInput} type="number" min="1" max="100000" step="1" value={filters.expected_attendees}
                  onChange={e => setFilters(f => ({ ...f, expected_attendees: e.target.value }))}
                  data-testid="filter-attendees"
                  className="w-28 px-3 py-2 border-2 border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A]" />
              </div>
              <div className="w-full sm:flex-1 min-w-0">
                <label className="text-sm font-semibold block mb-2">Bütçe modeli</label>
                <div className="flex flex-wrap gap-1.5">
                  {BUDGET_MODELS_CANON.map(b => (
                    <Chip key={b.value} active={filters.venue_budget_model === b.value}
                      onClick={() => setFilters(f => ({ ...f, venue_budget_model: f.venue_budget_model === b.value ? '' : b.value }))}
                      testId={`filter-budget-${b.value}`}
                    >{b.label}</Chip>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-2">İlçeler</label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto border-2 border-zinc-100 rounded-lg p-2 bg-white">
                {DISTRICTS_CANON.map(d => (
                  <button key={d} type="button" onClick={() => toggleList('preferred_districts', d)}
                    data-testid={`filter-district-${d}`}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      filters.preferred_districts.includes(d)
                        ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                        : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                    }`}>{d}</button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm font-semibold">Mekân bütçesi üst sınırı (TL)
                <input type="number" min="0" max="100000000" value={filters.venue_budget_max_try ?? ''} data-testid="filter-budget-max"
                  onChange={e => setFilters(f => ({...f, venue_budget_max_try: e.target.value === '' ? null : Number(e.target.value)}))}
                  placeholder="İsteğe bağlı" className="block w-full mt-2 px-3 py-2 border-2 border-zinc-200 rounded-lg" />
              </label>
              <label className="text-sm font-semibold">Mahalle / semt
                <input value={Array.isArray(filters.preferred_neighborhoods) ? filters.preferred_neighborhoods.join(', ') : filters.preferred_neighborhoods || ''} data-testid="filter-neighborhood"
                  onChange={e => setFilters(f => ({...f, preferred_neighborhoods: e.target.value}))}
                  placeholder="Örn. Ortaköy, Levent" className="block w-full mt-2 px-3 py-2 border-2 border-zinc-200 rounded-lg" />
              </label>
            </div>
            <details>
              <summary className="text-sm font-semibold cursor-pointer">İstemediğim yerler ve günler</summary>
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                {[['excluded_districts', 'İstemediğim ilçeler', 'Örn. Bakırköy'], ['excluded_neighborhoods', 'İstemediğim mahalle / semt', 'Örn. Ortaköy']].map(([key, label, placeholder]) => <label key={key} className="text-sm">{label}
                  <input value={Array.isArray(filters[key]) ? filters[key].join(', ') : filters[key] || ''} onChange={e => setFilters(f => ({...f, [key]: e.target.value}))}
                    placeholder={placeholder} data-testid={`filter-${key}`} className="block w-full mt-2 px-3 py-2 border-2 border-zinc-200 rounded-lg" />
                </label>)}
              </div>
              <p className="text-xs text-zinc-500 my-2">Birden fazla yeri virgülle ayır. Seçtiğin ilçelerle mahallelerin birbiriyle uyumlu olmalı.</p>
              <div className="flex flex-wrap gap-1.5">{DAYS_CANON.map(d => <Chip key={d.value} active={(filters.excluded_days || []).includes(d.value)} onClick={() => toggleList('excluded_days', d.value)} testId={`filter-exclude-day-${d.value}`}>{d.label} olmasın</Chip>)}</div>
            </details>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold block mb-2">Günler</label>
                <div className="flex flex-wrap gap-1.5">
                  {DAYS_CANON.map(d => (
                    <Chip key={d.value} active={filters.preferred_days.includes(d.value)}
                      onClick={() => toggleList('preferred_days', d.value)}
                      testId={`filter-day-${d.value}`}
                    >{d.label}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold block mb-2">Saat aralıkları</label>
                <div className="flex flex-wrap gap-1.5">
                  {TIME_RANGES_CANON.map(t => (
                    <Chip key={t.value} active={filters.preferred_time_ranges.includes(t.value)}
                      onClick={() => toggleList('preferred_time_ranges', t.value)}
                      testId={`filter-time-${t.value}`}
                    >{t.label}</Chip>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-2">Teknik ihtiyaçlar</label>
              <div className="flex flex-wrap gap-1.5">
                {TECH_CANON.map(t => (
                  <Chip key={t.value} active={filters.technical_needs.includes(t.value)}
                    onClick={() => toggleList('technical_needs', t.value)}
                    testId={`filter-tech-${t.value}`}
                  >{t.label}</Chip>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <TriChip label="Masa düzeni" value={filters.table_setup_needed}
                onChange={v => setFilters(f => ({ ...f, table_setup_needed: v }))}
                testIdBase="filter-tableSetup" />
              <TriChip label="Alkol servisi gerekli" value={filters.alcohol_needed}
                onChange={v => setFilters(f => ({ ...f, alcohol_needed: v }))}
                testIdBase="filter-alcohol" />
              <TriChip label="Dışarıdan malzeme" value={filters.outside_materials_needed}
                onChange={v => setFilters(f => ({ ...f, outside_materials_needed: v }))}
                testIdBase="filter-outside" />
            </div>

            <fieldset className="space-y-1" data-testid="filter-hard">
              <legend className="text-sm font-semibold mb-1">Olmazsa olmaz koşullar</legend>
              <p className="text-xs text-zinc-500 mb-1">İşaretlemediğin gün, bütçe ve teknik tercihler mekânı elemez; kartta uyarı olarak görünür.</p>
              {HARD_TOGGLES.map(([id, label, group, keys]) => (
                <label key={id} className="flex items-center gap-2 text-sm min-h-[44px]">
                  <input type="checkbox" checked={hardChecked(filters.hard, keys)} onChange={() => toggleHard(group, keys)} data-testid={`filter-hard-${id}`} />
                  {label}
                </label>
              ))}
            </fieldset>

            <button onClick={() => setFilters(emptyFilters)} data-testid="filters-reset"
              className="text-xs font-semibold text-zinc-500 hover:text-red-500">Filtreleri temizle</button>
          </div>
        )}

        {searching && <RequestProgress label={"Mekânlar aranıyor…"} />}
        {/* Results */}
        {results && (
          <div className="mt-8" data-testid="search-results">
            {results.filter_conflicts?.length > 0 && <div role="status" className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="filter-conflicts">
              <p className="text-sm font-semibold">{results.needs_filter_confirmation ? 'Yazdığın istekle seçili filtreler farklı. Aramadan önce hangi koşulları kullanacağımızı seç.' : 'Seçimin üzerine formdaki filtreler uygulandı.'}</p>
              <ul className="text-sm my-2">{results.filter_conflicts.map(item => <li key={item.field}>{FILTER_LABELS[item.field] || item.field}: seçili {filterValue(item.field, item.selected)} · metinde {filterValue(item.field, item.text)}</li>)}</ul>
              <div className="flex flex-wrap gap-2">
              {results.needs_filter_confirmation && <button type="button" className="action-secondary" disabled={searching || stale} onClick={() => runSearch(filters, true)}>Seçili filtrelerle ara</button>}
              <button type="button" className="action-secondary" disabled={searching || stale} onClick={() => {
                const updated = { ...filters, ...Object.fromEntries(results.filter_conflicts.map(item => [item.field, item.text])) };
                if (results.filter_conflicts.some(item => item.field === 'preferred_districts')) updated.preferred_neighborhoods = [];
                setFilters(updated); runSearch(updated);
              }}>Metindeki koşullarla yeniden ara</button>
              </div>
              {results.needs_filter_confirmation && <p className="text-xs mt-2">Seçim beklenirken arama hakkın kullanılmadı.</p>}
            </div>}

            {results.follow_up_questions?.length > 0 && (
              <div className="mb-4 bg-sky-50 border border-sky-200 rounded-xl p-4" data-testid="search-followups">
                <p className="text-sm font-medium text-sky-900 mb-1.5 flex items-center gap-1.5">
                  <Sparkles size={14} strokeWidth={1.75} /> Daha iyi sonuç için:
                </p>
                <ul className="space-y-0.5 text-sm text-sky-800 pl-5">
                  {results.follow_up_questions.slice(0, 3).map((q, i) => <li key={i}>· {q}</li>)}
                </ul>
              </div>
            )}
            {/* A7 (karar 8): istekte tür/bölge yoksa profilindeki çalışma bölgesi ve türleri kesin koşul olarak uygulanır. */}
            {!results.needs_filter_confirmation && Object.keys(results.criteria_defaults || {}).length > 0 && (
              <p className="text-xs text-zinc-600 mb-3" data-testid="search-dna-defaults">
                Profilinden uygulandı: {Object.values(results.criteria_defaults).map(d => d.label).filter(Boolean).join(' · ')}.
                {' '}Farklı bir ilçe veya etkinlik türü seçersen o geçerli olur.
              </p>
            )}
            {results.applied_filters && Object.keys(results.applied_filters).length > 0 && (
              <div className="mb-4 flex flex-wrap gap-1.5" data-testid="applied-filters">
                {appliedFilterLabels(results.applied_filters).map(label => <span key={label} className="text-xs px-2.5 py-1 bg-zinc-100 text-zinc-700 rounded-full">{label}</span>)}
              </div>
            )}

            <p className="text-sm text-zinc-600 mb-4" data-testid="results-count">
              {results.needs_filter_confirmation ? 'Koşullarını seçtiğinde arama başlayacak.' : results.total > 0
                ? <>Kriterlerine göre sıralanan <strong>{results.total}</strong> mekân bulundu{results.venues.length < results.total ? <>; ilk <strong>{results.venues.length}</strong> tanesi gösteriliyor</> : null}.</>
                : 'Filtrelerine uygun mekân bulunamadı. Kriterlerini genişletmeyi dene.'}
            </p>
            {!results.needs_filter_confirmation && !results.total && results.zero && (
              <CandidateZero zero={results.zero} onWiden={widen} disabled={searching || voiceBusy || moreBusy} testId="search-zero" />
            )}

            <div className="space-y-4">
              <VenueComparison venues={results.venues.filter(v=>compared.includes(v.id))} stale={stale} onClear={()=>setCompared([])}/>
              {results.venues.map(v => (
                <VenueCard key={v.id} v={v} onInterest={submitInterest} alreadyApplied={applied[v.id] || false} stale={stale || searching} onDetail={rememberDetail} compared={compared.includes(v.id)} compareFull={compared.length>=3} onCompare={()=>setCompared(old=>old.includes(v.id)?old.filter(id=>id!==v.id):old.length<3?[...old,v.id]:old)} trackRecord={trackRecords[v.id]} />
              ))}
            </div>
            {results.has_more && results.cursor && (
              <div className="candidate-more">
                <button type="button" className="action-secondary" onClick={loadMore} disabled={moreBusy || searching || stale} data-testid="search-more">
                  {moreBusy ? 'Getiriliyor…' : 'Daha fazla göster'}
                </button>
                <span className="text-xs text-zinc-500">Arama hakkı kullanmaz.</span>
              </div>
            )}
            {!results.has_more && results.limit_reached && <p className="text-xs text-zinc-500 mt-3" data-testid="search-limit-reached">Planındaki arama başına en fazla {results.result_limit} sonuç gösterildi. Daha belirgin filtrelerle aramanı daraltabilirsin.</p>}
            {results.pool_changed && <p className="text-xs text-zinc-500 mt-2" role="status">Havuz bu arada güncellendi; yeni arama yaparsan sıralama değişebilir.</p>}
          </div>
        )}

        {!results && !searching && (
          <div className="mt-10 border border-dashed border-zinc-200 rounded-2xl p-12 text-center animate-soft-lift stagger-2" data-testid="search-empty">
            <Search size={40} strokeWidth={1.25} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-sm text-zinc-500 max-w-md mx-auto">
              Yukarıdaki kutuya etkinliğini birkaç cümleyle anlat, uygun mekânları hemen görelim.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizerSearch;
